import { CANVAS_SIZE, type Canvas, type Channel, type PostFormat } from '@str/shared';
import { useState, type ReactNode, type CSSProperties } from 'react';
import { LoadingImage } from '@/components/LoadingImage';
import { mediaStyle } from '@/lib/scenes';

export type PreviewChannel = Channel | 'preview';

type Props = {
  channel: PreviewChannel;
  /** Frame width in px; the stage picks it from the room it has. */
  width?: number;
  format: PostFormat;
  /** Rendered card images in order (one for Image/Story/Reel, several for a carousel). */
  images: (string | null)[];
  videoUrl?: string | null;
  caption: string;
  name: string;
  handle: string;
  location: string;
  avatarUrl?: string | null;
  cardIdx?: number;
  onCardIdx?: (i: number) => void;
  canvas?: Canvas;
  onEdit?: () => void;
  pictureContent?: ReactNode;
};

const WIDTH = 360;
function previewAspect(canvas: Canvas = '4:5') {
  const size = CANVAS_SIZE[canvas];
  return `${size.width}/${size.height}`;
}

/**
 * What the post looks like where it is published: an Instagram feed post, story or reel, or a
 * Facebook Page post. The picture is the rendered card as-is; nothing here is baked into it.
 */
export function PostPreview(props: Props) {
  if (props.channel === 'preview' || props.channel === 'TT' || props.channel === 'YT')
    return <SimplePreview {...props} />;
  const vertical = props.format === 'Story' || props.format === 'Reel';
  if (props.channel === 'IG' && vertical) return <InstagramVertical {...props} />;
  if (props.channel === 'IG') return <InstagramFeed {...props} />;
  return <FacebookPost {...props} />;
}

function Avatar({ url, name, size = 32 }: { url?: string | null; name: string; size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: 99,
        flex: 'none',
        ...mediaStyle({ url, scene: 'dusk' }),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: size * 0.4,
        fontWeight: 700,
      }}
    >
      {url ? '' : name.slice(0, 1)}
    </span>
  );
}

function Picture({
  images,
  videoUrl,
  idx,
  aspect,
  style,
  onEdit,
  pictureContent,
}: {
  images: (string | null)[];
  videoUrl?: string | null;
  idx: number;
  aspect: string;
  style?: CSSProperties;
  onEdit?: () => void;
  pictureContent?: ReactNode;
}) {
  if (pictureContent)
    return (
      <div
        style={{
          width: '100%',
          aspectRatio: aspect,
          position: 'relative',
          overflow: 'hidden',
          ...style,
        }}
      >
        {pictureContent}
      </div>
    );
  const url = images[idx] ?? images[0] ?? null;
  // Every card is in the page, one shown, so the next is there when the host reaches it.
  const urls = [...new Set(images.filter((u): u is string => !!u))];
  return (
    <div
      role={onEdit ? 'button' : undefined}
      tabIndex={onEdit ? 0 : undefined}
      aria-label={onEdit ? 'Edit preview picture' : undefined}
      onClick={(e) => {
        e.stopPropagation();
        onEdit?.();
      }}
      onKeyDown={(e) => {
        if (onEdit && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onEdit();
        }
      }}
      style={{
        width: '100%',
        aspectRatio: aspect,
        position: 'relative',
        overflow: 'hidden',
        ...(url ? {} : mediaStyle({ scene: 'dusk' })),
        ...style,
      }}
    >
      {urls.map((u) => (
        <LoadingImage
          key={u}
          src={u}
          alt={`Picture ${images.indexOf(u) + 1} of ${images.length}`}
          hidden={u !== url}
          fill
        />
      ))}
      {/* Display-only playback needs no CORS; the canvas editor isolates its loads via corsSource. */}
      {videoUrl && (
        <video
          key={videoUrl}
          src={videoUrl}
          poster={url ?? undefined}
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
    </div>
  );
}

/** Caption with the platform's "more" fold. */
function Caption({
  handle,
  text,
  limit,
  color = '#262626',
  showHandle = true,
}: {
  handle: string;
  text: string;
  limit: number;
  color?: string;
  showHandle?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const folded = !open && text.length > limit;
  const shown = folded ? text.slice(0, limit).replace(/\s+\S*$/, '') : text;
  return (
    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.4, color, whiteSpace: 'pre-wrap' }}>
      {showHandle && <b style={{ marginRight: 5 }}>{handle}</b>}
      {linkify(shown)}
      {folded && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            border: 0,
            background: 'none',
            color: '#8e8e8e',
            padding: '0 0 0 4px',
            fontSize: 13,
          }}
        >
          … more
        </button>
      )}
    </p>
  );
}

/** Hashtags and links get the platform's blue; everything else is plain text. */
function linkify(text: string) {
  return text.split(/(#[\w]+|https?:\/\/\S+|\b[a-z0-9.-]+\.[a-z]{2,}\/\S+)/gi).map((part, i) =>
    /^(#|https?:\/\/)|\.[a-z]{2,}\//i.test(part) ? (
      <span key={i} style={{ color: '#00376b' }}>
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

function Icon({ d, size = 22 }: { d: string; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={d} />
    </svg>
  );
}
const HEART =
  'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z';
const COMMENT =
  'M21 11.5a8.4 8.4 0 0 1-9 8.4 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-4.8A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z';
const SEND = 'M22 2 11 13M22 2l-7 20-4-9-9-4z';
const SAVE = 'M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z';
const THUMB =
  'M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.3a2 2 0 0 0 2-1.7l1.4-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3';
const SHARE = 'M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13';

function InstagramFeed(p: Props) {
  const idx = p.cardIdx ?? 0;
  const many = p.images.length > 1;
  return (
    <div
      style={{
        width: p.width ?? WIDTH,
        flexShrink: 0,
        background: '#fff',
        border: '1px solid rgba(0,0,0,.1)',
        borderRadius: 12,
        overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        boxShadow: '0 30px 60px -30px rgba(0,0,0,.35)',
        animation: 'pop .4s both',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
        <Avatar url={p.avatarUrl} name={p.name} />
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#262626' }}>{p.handle}</span>
          <span style={{ fontSize: 11, color: '#262626' }}>{p.location}</span>
        </div>
        <span style={{ marginLeft: 'auto', color: '#262626', letterSpacing: 1 }}>···</span>
      </div>
      <div style={{ position: 'relative' }}>
        <Picture
          images={p.images}
          idx={idx}
          aspect={previewAspect(p.canvas)}
          pictureContent={p.pictureContent}
          onEdit={p.onEdit}
        />
        {many && (
          <>
            <span
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                background: 'rgba(0,0,0,.6)',
                color: '#fff',
                fontSize: 11,
                fontWeight: 600,
                padding: '3px 8px',
                borderRadius: 99,
              }}
            >
              {idx + 1}/{p.images.length}
            </span>
            {idx > 0 && (
              <button
                type="button"
                aria-label="Previous card"
                onClick={() => p.onCardIdx?.(idx - 1)}
                style={arrow('left')}
              >
                ‹
              </button>
            )}
            {idx < p.images.length - 1 && (
              <button
                type="button"
                aria-label="Next card"
                onClick={() => p.onCardIdx?.(idx + 1)}
                style={arrow('right')}
              >
                ›
              </button>
            )}
          </>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '10px 12px 6px',
          color: '#262626',
        }}
      >
        <Icon d={HEART} />
        <Icon d={COMMENT} />
        <Icon d={SEND} />
        {many && (
          <span style={{ display: 'flex', gap: 4, margin: '0 auto' }}>
            {p.images.map((_, i) => (
              <span
                key={i}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 99,
                  background: i === idx ? '#0095f6' : '#dbdbdb',
                }}
              />
            ))}
          </span>
        )}
        <span style={{ marginLeft: many ? 0 : 'auto' }}>
          <Icon d={SAVE} />
        </span>
      </div>
      <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Caption handle={p.handle} text={p.caption} limit={125} />
        <span
          style={{
            fontSize: 11,
            color: '#8e8e8e',
            textTransform: 'uppercase',
            letterSpacing: '.02em',
          }}
        >
          Just now
        </span>
      </div>
    </div>
  );
}

function arrow(side: 'left' | 'right'): CSSProperties {
  return {
    position: 'absolute',
    top: '50%',
    [side]: 8,
    transform: 'translateY(-50%)',
    width: 26,
    height: 26,
    borderRadius: 99,
    border: 0,
    background: 'rgba(255,255,255,.9)',
    color: '#262626',
    fontSize: 18,
    lineHeight: '26px',
    cursor: 'pointer',
  };
}

function InstagramVertical(p: Props) {
  const reel = p.format === 'Reel';
  return (
    <div
      style={{
        width: 300,
        flexShrink: 0,
        aspectRatio: '9/16',
        borderRadius: 28,
        overflow: 'hidden',
        position: 'relative',
        background: '#000',
        boxShadow: '0 40px 80px -30px rgba(0,0,0,.5), 0 0 0 8px #111',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        animation: 'pop .4s both',
      }}
    >
      <Picture
        images={p.images}
        videoUrl={p.videoUrl}
        idx={0}
        aspect="9/16"
        pictureContent={p.pictureContent}
        onEdit={p.onEdit}
        style={{ position: 'absolute', inset: 0 }}
      />
      <div
        onClick={p.onEdit}
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(0,0,0,.35) 0%, transparent 25%, transparent 60%, rgba(0,0,0,.55) 100%)',
        }}
      />
      {!reel && (
        <div
          style={{ position: 'absolute', top: 10, left: 10, right: 10, display: 'flex', gap: 3 }}
        >
          {[0.95, 0.35, 0.35].map((o, i) => (
            <span
              key={i}
              style={{
                flex: 1,
                height: 2.5,
                borderRadius: 99,
                background: `rgba(255,255,255,${o})`,
              }}
            />
          ))}
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          top: reel ? 14 : 20,
          left: 12,
          right: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: '#fff',
        }}
      >
        {!reel && (
          <>
            <Avatar url={p.avatarUrl} name={p.name} size={28} />
            <span style={{ fontSize: 13, fontWeight: 700 }}>{p.handle}</span>
            <span style={{ fontSize: 12, opacity: 0.8 }}>Just now</span>
          </>
        )}
        {reel && <span style={{ fontSize: 16, fontWeight: 700 }}>Reels</span>}
      </div>
      {reel && (
        <div
          style={{
            position: 'absolute',
            right: 10,
            bottom: 90,
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
            color: '#fff',
            alignItems: 'center',
          }}
        >
          <Icon d={HEART} size={26} />
          <Icon d={COMMENT} size={26} />
          <Icon d={SEND} size={26} />
          <span style={{ letterSpacing: 1 }}>···</span>
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          left: 12,
          right: reel ? 56 : 12,
          bottom: reel ? 18 : 14,
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {reel && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar url={p.avatarUrl} name={p.name} size={28} />
            <span style={{ fontSize: 13, fontWeight: 700 }}>{p.handle}</span>
          </div>
        )}
        {reel ? (
          <Caption handle={p.handle} text={p.caption} limit={80} color="#fff" showHandle={false} />
        ) : (
          <div
            style={{
              border: '1px solid rgba(255,255,255,.7)',
              borderRadius: 99,
              padding: '9px 14px',
              fontSize: 13,
              opacity: 0.9,
            }}
          >
            Send message
          </div>
        )}
      </div>
    </div>
  );
}

function FacebookPost(p: Props) {
  const idx = p.cardIdx ?? 0;
  const vertical = p.format === 'Story' || p.format === 'Reel';
  return (
    <div
      style={{
        width: p.width ?? WIDTH,
        flexShrink: 0,
        background: '#fff',
        borderRadius: 10,
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0,0,0,.2), 0 30px 60px -30px rgba(0,0,0,.35)',
        fontFamily: 'Helvetica, Arial, sans-serif',
        animation: 'pop .4s both',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 12px 8px' }}>
        <Avatar url={p.avatarUrl} name={p.name} size={40} />
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#050505' }}>{p.name}</span>
          <span style={{ fontSize: 12, color: '#65676b' }}>Just now · 🌎</span>
        </div>
        <span style={{ marginLeft: 'auto', color: '#65676b', letterSpacing: 1 }}>···</span>
      </div>
      <div style={{ padding: '0 12px 10px' }}>
        <Caption handle={p.name} text={p.caption} limit={200} color="#050505" showHandle={false} />
      </div>
      <div style={{ position: 'relative', background: '#000' }}>
        <Picture
          images={p.images}
          videoUrl={p.videoUrl}
          idx={idx}
          aspect={vertical ? '9/16' : previewAspect(p.canvas)}
          pictureContent={p.pictureContent}
          onEdit={p.onEdit}
          style={vertical ? { width: '60%', margin: '0 auto' } : undefined}
        />
        {p.images.length > 1 && (
          <>
            {idx > 0 && (
              <button
                type="button"
                aria-label="Previous card"
                onClick={() => p.onCardIdx?.(idx - 1)}
                style={arrow('left')}
              >
                ‹
              </button>
            )}
            {idx < p.images.length - 1 && (
              <button
                type="button"
                aria-label="Next card"
                onClick={() => p.onCardIdx?.(idx + 1)}
                style={arrow('right')}
              >
                ›
              </button>
            )}
          </>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-around',
          padding: '6px 8px',
          borderTop: '1px solid #e4e6eb',
          color: '#65676b',
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon d={THUMB} size={18} /> Like
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon d={COMMENT} size={18} /> Comment
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon d={SHARE} size={18} /> Share
        </span>
      </div>
    </div>
  );
}

function SimplePreview(p: Props) {
  const label =
    p.channel === 'TT' ? 'TikTok preview' : p.channel === 'YT' ? 'YouTube preview' : 'Post preview';
  const idx = p.cardIdx ?? 0;
  return (
    <div
      aria-label={label}
      style={{
        width: p.width ?? WIDTH,
        maxWidth: '100%',
        flexShrink: 0,
        background: '#fff',
        borderRadius: 16,
        overflow: 'hidden',
        border: '1px solid #ddd',
      }}
    >
      <div style={{ padding: 12, fontSize: 12 }}>
        {label}
        {p.channel !== 'preview' && ` · ${p.handle}`}
      </div>
      <Picture
        images={p.images}
        videoUrl={p.videoUrl}
        idx={idx}
        aspect={p.format === 'Reel' || p.format === 'Story' ? '9/16' : previewAspect(p.canvas)}
        pictureContent={p.pictureContent}
        onEdit={p.onEdit}
      />
      {p.images.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, padding: 8 }}>
          <button
            aria-label="Previous card"
            disabled={idx === 0}
            onClick={() => p.onCardIdx?.(idx - 1)}
          >
            ←
          </button>
          <span>
            {idx + 1} / {p.images.length}
          </span>
          <button
            aria-label="Next card"
            disabled={idx >= p.images.length - 1}
            onClick={() => p.onCardIdx?.(idx + 1)}
          >
            →
          </button>
        </div>
      )}
      <p style={{ padding: 12, margin: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}>{p.caption}</p>
    </div>
  );
}
